import { Fragment, useContext, useEffect, useState } from "react"
import { UserContext } from "../../../../../../context/UserContext"
import { useNavigate } from "react-router-dom"
import { Stack, Typography, CircularProgress } from "@mui/material"
import { FormContainer, NavButtons } from "../../../../Menu/Flow"
import { FlowHandlersContext } from "../../../../Menu/FlowContainer"
import { PlansTable } from "../components"
import SelectPlanType from "../components/SelectPlanType"
import api from "../../../../../../api/axios"
import employerFlowStyles from "../../styles"

function buildTouchedFromErrors(errors) {
	if (!errors || typeof errors !== "object") {
		return true
	}

	if (Array.isArray(errors)) {
		return errors.map((item) => buildTouchedFromErrors(item))
	}

	return Object.keys(errors).reduce((acc, key) => {
		acc[key] = buildTouchedFromErrors(errors[key])
		return acc
	}, {})
}

export default function Plans(props) {
	const navigate = useNavigate()
	const { mode, exitUrl, step, formik, onboarding } = props
	const { setRefreshUserProfile, refreshUserProfile, user, getEmployerPlans, getEmployer, isSeanOrCustomer } = useContext(UserContext)
	const handlers = useContext(FlowHandlersContext)

	const [enableNext, setEnableNext] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [isNavigating, setIsNavigating] = useState(false)

	let plans = getEmployerPlans()
	let employer = getEmployer()
	const employerId = employer?.id

	// Function to calculate if Next should be enabled
	const calculateEnableNext = (plansList) => {
		if (!plansList || plansList.length === 0) {
			return true // No plans yet, can proceed
		}

		// Check if at least one plan is completed
		for (const plan of plansList) {
			if (plan?.isComplete) {
				return true
			}
		}
		return false
	}

	async function getData() {
		try {
			setIsLoading(true)
			await refreshUserProfile()
			// After refresh, get fresh plans and recalculate enableNext
			const freshPlans = getEmployerPlans()
			const shouldEnableNext = calculateEnableNext(freshPlans)
			setEnableNext(shouldEnableNext)
		} catch (error) {
			console.error(error)
		} finally {
			setIsLoading(false)
		}
	}

	useEffect(() => {
		if (user && !isNavigating) {
			getData()
		}
	}, [user?.id])

	// Recalculate enableNext whenever plans change (but not when navigating)
	useEffect(() => {
		if (!isNavigating) {
			const shouldEnableNext = calculateEnableNext(plans)
			setEnableNext(shouldEnableNext)
		}
	}, [plans?.length, plans])

	async function handleNext(event) {
		// Prevent formik submission
		if (event) {
			event.preventDefault()
			event.stopPropagation()
		}

		const newPlanType = formik.values.newPlan
		console.log("Plans Step: handleNext called, newPlanType =", newPlanType)
		if (!newPlanType || newPlanType.length === 0) {
			let completedPlan = false;
			if (employer?.employerPlans?.length > 0) {
				for (const plan of employer.employerPlans) {
					if (plan?.isComplete) {
						completedPlan = true;
						break;
					}
				}
			}
			if (completedPlan) {
				if (handlers?.onNext) {
					await handlers.onNext()
				}
			}
		}

		const errors = await formik.validateForm()

		if (Object.keys(errors).length > 0) {
			formik.setTouched(buildTouchedFromErrors(errors), true)
			console.log("Plans Step: validation errors", errors)
			return false
		}

		if (newPlanType?.length > 0) {
			// Set navigating flag to prevent re-renders
			setIsNavigating(true)
			
			// Get current date components
			const now = new Date();
			const currentYear = now.getFullYear();
			const currentMonth = now.getMonth() + 1; // getMonth() is 0-indexed

			// Create start date (first day of current month)
			const startYear = currentYear;
			const startMonth = currentMonth;
			const startDate = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;

			// Calculate end date (last day of month 11 months from now)
			let endMonth = currentMonth + 11;
			let endYear = currentYear;
			if (newPlanType === "ICHRA") {
				endMonth = 12;
			}

			// Adjust year if month exceeds 12
			if (endMonth > 12) {
				endYear += Math.floor(endMonth / 12);
				endMonth = endMonth % 12;
				if (endMonth === 0) {
					endMonth = 12;
					endYear -= 1;
				}
			}

			// Get last day of the end month
			const lastDayOfMonth = new Date(endYear, endMonth, 0).getDate();
			const endDate = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

			const year = endYear;

			const url = `/employer/${employer.id}/plan/v2/${newPlanType}`
			const payload = {
				name: `${year} ${newPlanType}`,
				startDate: startDate,
				endDate: endDate,
			}
			console.log("Creating new plan with payload:", payload)
			try {
				const response = await api.post(url, payload)
				const newUserPlan = response.data
				console.log("Plan created successfully:", newUserPlan)
				// Refresh user profile but don't wait for re-render
				setRefreshUserProfile()
				refreshUserProfile().then(() => {
					// Navigate after refresh completes
					const nextUrl = `/employer-flow/plans?onboarding=${onboarding}&step=${step + 1}&planId=${newUserPlan.id}`
					navigate(nextUrl)
				})
			} catch (error) {
				console.error("Error creating plan:", error)
				setIsNavigating(false)
			}
		}

		// Return false to prevent form submission
		return false
	}

	// If navigating, show the current view frozen (don't update)
	const showPlansTable = Boolean(plans?.length > 0) && !isNavigating

	// Format date to "MMM YYYY" format (e.g., "Nov 2025")
	const formatDate = (dateString) => {
		if (!dateString) return ""
		// Parse the date string manually to avoid timezone issues
		const [year, month, day] = dateString.split('-').map(Number)
		const date = new Date(year, month - 1, day) // month is 0-indexed in Date constructor
		const options = { month: "short", year: "numeric" }
		return date.toLocaleDateString("en-US", options)
	}
	let hideNext = false
	if (mode === "manage" && showPlansTable && isSeanOrCustomer) {
		hideNext = true
	}

	return (
		<Stack spacing="30px" height="100%">
			<FormContainer>
				<Typography variant="h2" sx={employerFlowStyles.h2}>HRA Plans</Typography>

				{(!showPlansTable || !isSeanOrCustomer) && (
					<Fragment>
						<Typography>Select the type of plan you want to offer your employees.</Typography>
						<SelectPlanType formik={formik} employer={employer} />
					</Fragment>
				)}

				{showPlansTable && (
					<Fragment>
						<Typography sx={{ marginTop: "20px" }}></Typography>
						<PlansTable {...props} plans={plans} formatDate={formatDate} />
					</Fragment>
				)}
			</FormContainer>

			<NavButtons mode={mode} exitUrl={exitUrl} step={step} disabled={!enableNext || isNavigating} onNextStep={handleNext} hideNext={hideNext} />
		</Stack>
	)
}